import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { Icon24ErrorCircleOutline } from '@vkontakte/icons';
import classNames from 'clsx';
import { ReactComponent as WarningIcon } from 'mrg-icons/src/mailru/status/warning_triangle.svg';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ENABLE_FULL_RESPONSIVE, IS_B2B_BIZ_USER, NEW_PORTAL_HEADER } from 'reactApp/appHelpers/configHelpers';
import { FORCE_DISABLE_BIG_TRASHBIN_SIZE_INFO } from 'reactApp/appHelpers/featuresHelpers/features/bigTrashbinSizeInfo';
import { isSafeEnabled } from 'reactApp/appHelpers/featuresHelpers/features/overquotaSafe';
import { restoreTrashbinFiles } from 'reactApp/appHelpers/featuresHelpers/features/restoreTrashbinFiles';
import { renderEmptyTrashBinDialog } from 'reactApp/components/EmptyTrashBin/EmptyTrashBin.helpers';
import { BREAKPOINT_SM } from 'reactApp/constants/breakpoints';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import { useGoToSubscriptions } from 'reactApp/hooks/useGoToSubscriptions';
import { useWindowSize } from 'reactApp/hooks/useWindowSize';
import { getUserOverquotaState } from 'reactApp/modules/profile/profile.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { sendXrayForSafeOpen } from 'reactApp/modules/trashbin/safe.helpers';
import { toggleBin, trashbinLoad } from 'reactApp/modules/trashbin/trashbin.module';
import {
    getBins,
    getOpenedBinId,
    getSafeSize,
    getTrashbinSize,
    isLargeTrashbinInfoVisible,
    isNeedShowRestoreFromSafeButton,
} from 'reactApp/modules/trashbin/trashbin.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { SafeManageButton } from 'reactApp/sections/TrashbinPage/components/SafeManageButton';
import { BreadcrumbsContainer } from 'reactApp/ui/BreadcrumbsContainer/BreadcrumbsContainer';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { DataList } from 'reactApp/ui/Datalist/DataList';
import { FilesCounter } from 'reactApp/ui/FilesCounter/FilesCounter';
import { Informer } from 'reactApp/ui/Informer/Informer';
import { EInformerColor } from 'reactApp/ui/Informer/Informer.types';
import { InlineInformer } from 'reactApp/ui/Informer/InlineInformer/InlineInformer';
import { PageTabMenu } from 'reactApp/ui/PageTabMenu/PageTabMenu';
import { RestoreTrashbinFilesInformer } from 'reactApp/ui/RestoreTrashbinFilesInformer/RestoreTrashbinFilesInformer';
import { Icon20SafeBoxOutline } from 'reactApp/ui/VKUIIcons';
import { sendGa } from 'reactApp/utils/ga';
import { noop } from 'reactApp/utils/helpers';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './TrashbinPage.css';

export const TrashbinPage = () => {
    const bins = useSelector(getBins);
    const dispatch = useDispatch();
    const openedBin = useSelector(getOpenedBinId);
    const trashbinSize = useSelector(getTrashbinSize);
    const safeSize = useSelector(getSafeSize);
    const showOverquotaInfo = useSelector(UserQuotaSelectors.isOverquota);
    const tabs = useMemo(() => bins.map((bin) => ({ value: bin.id, name: bin.name })), [bins]);
    const showTabs = bins.length > 1;
    const showLargeTrashbinInfo = useSelector(isLargeTrashbinInfoVisible) && !FORCE_DISABLE_BIG_TRASHBIN_SIZE_INFO;
    const isUserInOverquota = useSelector(UserQuotaSelectors.isOverquota);
    const quotaState = useSelector(getUserOverquotaState);
    const isShowReturnFromSafeButton = useSelector(isNeedShowRestoreFromSafeButton);

    const currentTabName = tabs.find((tab) => tab.value === openedBin)?.name || '';
    const { goToSubscriptions } = useGoToSubscriptions();
    const { windowWidth } = useWindowSize();

    const handleSetCurrentBin = useCallback(
        (id) => {
            dispatch(toggleBin({ id: openedBin === id ? '' : id }));
        },
        [dispatch, openedBin]
    );

    useEffect(() => {
        sendPaymentGa({ eventCategory: ECategoryGa.entered, action: 'basket' });
        sendPaymentGa({ eventCategory: ECategoryGa.entered, action: 'page-entered', source: 'basket' });

        dispatch(trashbinLoad());
    }, []);

    useEffect(() => {
        if (quotaState?.status === 'loaded') {
            sendXrayForSafeOpen(true, quotaState.state, isUserInOverquota);
        }
    }, [quotaState?.status]);

    const handleInformerClick = useCallback(() => {
        sendGa('overquota-informer', 'click');
        goToSubscriptions({ inSameTab: false, query: PaymentUTM.trashbinInformer, isNewTariffsPage: true });
    }, []);

    const handleTrashbinClearClick = useCallback(() => {
        renderEmptyTrashBinDialog();
    }, []);

    return (
        <div
            className={classNames(styles.root, {
                [styles.root_new_portal_header]: NEW_PORTAL_HEADER,
            })}
        >
            {!ENABLE_FULL_RESPONSIVE || (ENABLE_FULL_RESPONSIVE && windowWidth > BREAKPOINT_SM) ? (
                <div
                    className={classNames(styles.title, {
                        [styles.title_new_portal_header]: NEW_PORTAL_HEADER,
                    })}
                >
                    <BreadcrumbsContainer storage={EStorageType.trashbin} />
                    {!showOverquotaInfo && (
                        <div className={styles.informer}>
                            {restoreTrashbinFiles === 'b' || restoreTrashbinFiles === 'c' ? (
                                <RestoreTrashbinFilesInformer />
                            ) : (
                                <InlineInformer color={EInformerColor.newTransparent} newRoot>
                                    Файлы удаляются автоматически через 14 дней после перемещения в корзину
                                </InlineInformer>
                            )}
                        </div>
                    )}
                    <div className={styles.counter}>{Boolean(openedBin) && <FilesCounter />}</div>
                </div>
            ) : (
                <div className={styles.titleResponsive}>
                    <div className={styles.titleResponsiveHeader}>
                        <BreadcrumbsContainer storage={EStorageType.trashbin} />
                        <div className={classNames(styles.counter, styles.counterResponsive)}>{Boolean(openedBin) && <FilesCounter />}</div>
                    </div>
                    {!showOverquotaInfo && (
                        <div className={classNames(styles.informer, styles.informerResponsive)}>
                            <InlineInformer className={styles.inlineInformerResponsive} color={EInformerColor.newTransparent} newRoot>
                                Файлы удаляются автоматически через 14 дней после перемещения в корзину
                            </InlineInformer>
                        </div>
                    )}
                </div>
            )}

            {showOverquotaInfo && !isSafeEnabled && (
                <div className={styles.informerOverquota}>
                    <Informer
                        dataQaId="informer-trash-overq"
                        color={EInformerColor.red}
                        icon={<WarningIcon height={16} width={18} fill="var(--vkui--color_text_negative--hover)" />}
                    >
                        <div className={styles.informerContent}>
                            Корзина очищается автоматически по прошествии 2 недель.
                            <br />В корзине видны те файлы, для восстановления которых в Облаке хватает места.{' '}
                            <>
                                {IS_B2B_BIZ_USER ? <>Увеличьте Облако </> : <span onClick={handleInformerClick}>Увеличьте Облако</span>},
                                чтобы увидеть и восстановить все файлы
                            </>
                        </div>
                    </Informer>
                </div>
            )}

            {showLargeTrashbinInfo && !showOverquotaInfo && (
                <div className={styles.informerTrashbinSize}>
                    <Informer
                        dataQaId="informer-big-trashbin"
                        color={EInformerColor.red}
                        icon={<Icon24ErrorCircleOutline fill="var(--vkui--color_text_negative--hover)" />}
                        title={<>Очистите корзину и&nbsp;освободите {bytesToNDigits(trashbinSize, 3).value} в&nbsp;Облаке</>}
                        button={
                            <Button
                                qaId="trashbin-large-banner-button"
                                sizeMode={ButtonSizeMode.middle}
                                error
                                className={styles.informerTrashbinSizeButton}
                                onClick={handleTrashbinClearClick}
                            >
                                Очистить корзину
                            </Button>
                        }
                    >
                        <p className={styles.informerTrashbinSizeText}>
                            Файлы удаляются автоматически через 14&nbsp;дней после перемещения в&nbsp;корзину, но&nbsp;вы&nbsp;можете
                            удалить их&nbsp;сейчас
                        </p>
                    </Informer>
                </div>
            )}

            {isSafeEnabled && isShowReturnFromSafeButton && (
                <div className={styles.informerTrashbinSafe}>
                    <Informer
                        dataQaId="informer-safe-trashbin"
                        color={EInformerColor.base}
                        icon={<Icon20SafeBoxOutline color="var(--vkui--color_icon_accent)" />}
                        title={
                            <p className={styles.informerSafeTitle}>
                                В сейфе — файлы из корзины весом {safeSize ? bytesToNDigits(safeSize, 3).value : ''}
                            </p>
                        }
                        button={<SafeManageButton />}
                    >
                        <p className={styles.informerSafeText}>Они не занимают место в Облаке. Можно вернуть, когда будут нужны</p>
                    </Informer>
                </div>
            )}

            {showTabs && (
                <div className={styles.menu}>
                    <PageTabMenu current={openedBin} tabs={tabs} onClick={handleSetCurrentBin} noVerticalScroll />
                </div>
            )}
            <div
                className={classNames(styles.currentBin, { [styles.currentBin_new_portal_header]: NEW_PORTAL_HEADER })}
                data-qa-name={currentTabName}
            >
                <DataList goTo={noop} storage={EStorageType.trashbin} />
            </div>
        </div>
    );
};
